import { useCallback, useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import { useRouter } from "next/router";

import { AxiosInstanceInterceptorRequestExtensionI } from "@/api/interceptors/interface";
import { ERROR_CODES } from "@/constants/errors";
import Router from "@/helpers/router";
import { checkIfClient } from "shared/lib/helpers";
import { useGlobalContext } from "@/hooks/use-global-context";
import { LoadingModule } from "@/components/modules/loading";
import { USER_APPLICATION_STATUS } from "@/constants/signup";
import { APII, glencocoClientAPI } from "@/api/glencoco";
import { queryClient } from "@/api/query-client";
import { fetchManageMeQuery } from "@/hooks/use-manage-me";
import { firstManagePage, isManagePath } from "@/lib/manage/landing";

/**
 * Use in Axios error interceptors but logic should be stored
 * in context relevant environment
 *
 * @param e
 */
export const redirectOnAxiosErrorInterceptor = (
  e: AxiosError<any> & {
    config?: AxiosInstanceInterceptorRequestExtensionI;
  }
) => {
  if (
    e?.response?.data?.error_code === ERROR_CODES.PROFILE_BLOCKED &&
    !window.location.href.includes(Router.profileInactive())
  ) {
    window.location.href = Router.profileInactive();
  }
};

/** Full-page navigations of the gate, behind one seam so tests can watch them. */
export const redirectNavigation = {
  goTo: (url: string) => {
    window.location.href = url;
  },
};

export const RedirectsProvider = ({ children }: { children?: any }) => {
  const { user: auth0User } = useAuth0();
  const { glencocoUser, isUserLoaded, userApplicationStatus } =
    useGlobalContext();

  const [isRedirecting, setIsRedirecting] = useState(true);

  const router = useRouter();
  // This provider sits outside ApiClientProvider, and SignUpLayout has no
  // QueryClientProvider above it: use the app's module-level client (the one
  // AppLayout provides, so the cache is shared with useManageMe) and build an
  // API client only when a redirect actually needs to ask manage/me.
  const manageApiRef = useRef<Pick<APII, "manage"> | null>(null);
  // Set while a role holder who fails the applicant gate is let through to a
  // manage page: leaving /manage/* sends them back there.
  const roleHolderLandingRef = useRef<string | null>(null);

  /**
   * C-14 (C14-F1-D31): before the applicant gate sends someone away, ask
   * whether they hold a role. A role holder stays on a manage page, or lands
   * on their first one. Anything else (no role, no manage page yet, the
   * switch off, a network error, a 5xx) redirects exactly as before.
   */
  const redirectUnlessRoleHolder = useCallback((redirect: () => void) => {
    if (!manageApiRef.current) {
      manageApiRef.current = glencocoClientAPI();
    }

    fetchManageMeQuery(queryClient, manageApiRef.current)
      .then(({ me }) => {
        const landing = firstManagePage(me);

        if (!landing) {
          roleHolderLandingRef.current = null;
          redirect();
          return;
        }

        roleHolderLandingRef.current = landing;

        if (isManagePath(window.location.pathname)) {
          setIsRedirecting(false);
          return;
        }

        setIsRedirecting(true);
        redirectNavigation.goTo(landing);
      })
      .catch(() => redirect());
  }, []);

  // The gate's effect doesn't re-run on client-side navigation, so a role
  // holder let through to /manage/* is sent back when they leave it.
  useEffect(() => {
    const onRouteChange = (url: string) => {
      const landing = roleHolderLandingRef.current;

      if (landing && !isManagePath(url.split(/[?#]/)[0])) {
        setIsRedirecting(true);
        redirectNavigation.goTo(landing);
      }
    };

    router?.events?.on("routeChangeComplete", onRouteChange);

    return () => {
      router?.events?.off("routeChangeComplete", onRouteChange);
    };
  }, [router]);

  useEffect(() => {
    if (checkIfClient()) {
      const url = new URL(window.location.href);

      //User is not Verified
      if (
        !auth0User?.email_verified &&
        !url.pathname.includes(Router.verify())
      ) {
        setIsRedirecting(true);

        const search = window.location.search
          ? `${window.location.search}`
          : "";

        window.location.href = `${Router.verify()}${search}`;
        return;
      }

      // Handle ATS redirect if user has not applied yet through the landing page
      if (auth0User?.email_verified && userApplicationStatus) {
        if (USER_APPLICATION_STATUS.NO_APPLICATION === userApplicationStatus) {
          redirectUnlessRoleHolder(() => {
            setIsRedirecting(true);
            redirectNavigation.goTo(Router.glencocoOpenMarketplace());
          });
          return;
        }
      }

      // If isUserLoaded is true and glencocoUser is undefined, this means the user is blocked from the platform.
      // Refer to /lib/api/glencoco.ts response interceptors to see how blocked users are handled.

      if (isUserLoaded && glencocoUser) {
        //User is not Registered, or they've been waitlisted: send user to signup flow to complete registration.
        // If they've been waitlisted, the signup flow will take care of showing the appropriate "waitlisted" UI
        if (
          (!glencocoUser?.registered ||
            USER_APPLICATION_STATUS.WAITLISTED === userApplicationStatus) &&
          [Router.signup(), Router.verify()].every(
            (route) => !url.pathname.includes(route)
          )
        ) {
          redirectUnlessRoleHolder(() => {
            setIsRedirecting(true);
            redirectNavigation.goTo(Router.signup());
          });
          return;
        }

        //User is registered but trying to access signup page
        if (
          glencocoUser?.registered &&
          USER_APPLICATION_STATUS.ACCEPTED === userApplicationStatus &&
          url.pathname.includes(Router.signup())
        ) {
          setIsRedirecting(true);
          window.location.href = Router.home();
          return;
        }

        setIsRedirecting(false);
      }
    }
  }, [
    auth0User?.email_verified,
    glencocoUser?.registered,
    isUserLoaded,
    userApplicationStatus,
  ]);

  return isRedirecting ? <LoadingModule /> : children;
};
